import React from 'react';
import { Calendar, MessageSquare, Paperclip, CheckSquare, FileText } from 'lucide-react';
import { TaskBoardTask } from '../../types';
import { PRIORITY_COLORS, ProjectPriority, isTaskDone } from '../../utils/projectConstants';
import { formatArgentinaDate } from '../../utils/dateUtils';
import { formatAggregationNumber } from '../../utils/projectUtils';
import { getChecklistProgress } from '../../utils/projectUtils';
import {
  formatAssigneeDisplay,
  getAssigneeAvatarStyle,
  getAssigneeInitialLetter,
} from '../../utils/projectAssignees';
import {
  getFileAttachments,
  getImageAttachments,
  safeProjectAttachments,
} from '../../utils/projectAttachments';

interface ProjectTaskCardProps {
  task: TaskBoardTask;
  onClick: () => void;
  isDragging?: boolean;
}

const ProjectTaskCard: React.FC<ProjectTaskCardProps> = ({ task, onClick, isDragging }) => {
  const priority = (task.priority || 'medium') as ProjectPriority;
  const { done: checklistDone, total } = getChecklistProgress(task);
  const commentsCount = task.comments?.length || 0;
  const attachments = safeProjectAttachments(task.attachments);
  const imageAttachments = getImageAttachments(task.attachments);
  const fileAttachments = getFileAttachments(task.attachments);
  const attachmentsCount = attachments.length;
  const tags = (task.tags || []).slice(0, 2);

  const isOverdue = task.due_date && !task.completed && new Date(task.due_date) < new Date(new Date().toDateString());
  const taskDone = isTaskDone(task.status, task.completed);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={e => {
        if (isDragging) return;
        e.stopPropagation();
        onClick();
      }}
      onKeyDown={e => { if (e.key === 'Enter') onClick(); }}
      className={`w-full text-left group rounded-lg border px-2.5 py-2 transition-all select-none ${
        taskDone
          ? 'opacity-50 bg-gray-50/90 dark:bg-gray-800/40 border-gray-200/50 dark:border-gray-700/40 cursor-default'
          : `bg-white dark:bg-gray-900/80 hover:shadow-md hover:border-gray-300 dark:hover:border-gray-600 cursor-grab active:cursor-grabbing ${
              isDragging
                ? 'shadow-lg ring-2 ring-indigo-400/40 rotate-1 opacity-90'
                : 'shadow-sm border-gray-200/80 dark:border-gray-700/60'
            }`
      }`}
    >
      <div className="flex items-start gap-1.5 pointer-events-none">
        <span
          className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${taskDone ? 'opacity-40' : ''}`}
          style={{ backgroundColor: taskDone ? '#94a3b8' : PRIORITY_COLORS[priority] }}
          title={priority}
        />
        <div className="flex-1 min-w-0">
          <div className="flex gap-1 min-w-0">
            <p className={`flex-1 min-w-0 text-[13px] leading-snug line-clamp-2 ${
              taskDone
                ? 'line-through text-gray-400 dark:text-gray-500 font-normal'
                : 'font-medium text-gray-900 dark:text-gray-100'
            }`}>
              {task.title}
            </p>
            {task.assignee && (
              <span
                className="flex-shrink-0 self-end w-5 h-5 rounded-full text-[9px] font-semibold flex items-center justify-center"
                style={getAssigneeAvatarStyle(task.assignee)}
                title={formatAssigneeDisplay(task.assignee)}
              >
                {getAssigneeInitialLetter(task.assignee)}
              </span>
            )}
          </div>

          {imageAttachments.length > 0 && (
            <div className={`flex items-center gap-1 mt-1.5 ${taskDone ? 'opacity-60' : ''}`}>
              {imageAttachments.slice(0, 3).map(a => (
                <img
                  key={a.url}
                  src={a.url}
                  alt={a.name || ''}
                  className="w-9 h-9 rounded object-cover border border-gray-200/80 dark:border-gray-700/80 flex-shrink-0"
                />
              ))}
              {imageAttachments.length > 3 && (
                <span className="text-[9px] text-gray-400 flex-shrink-0">+{imageAttachments.length - 3}</span>
              )}
            </div>
          )}

          {fileAttachments.length > 0 && imageAttachments.length === 0 && (
            <div className={`flex items-center gap-1 mt-1.5 min-w-0 ${taskDone ? 'opacity-60' : ''}`}>
              <FileText className="w-3 h-3 text-gray-400 flex-shrink-0" />
              <span className="text-[10px] text-gray-500 truncate">
                {fileAttachments[0].name || 'Archivo'}
                {fileAttachments.length > 1 ? ` +${fileAttachments.length - 1}` : ''}
              </span>
            </div>
          )}

          {tags.length > 0 && (
            <div className={`flex flex-wrap gap-1 mt-1.5 ${taskDone ? 'opacity-60' : ''}`}>
              {tags.map(tag => (
                <span
                  key={tag}
                  className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
                >
                  {tag}
                </span>
              ))}
              {(task.tags?.length || 0) > 2 && (
                <span className="text-[10px] text-gray-400">+{(task.tags?.length || 0) - 2}</span>
              )}
            </div>
          )}

          <div className={`flex items-center mt-2 gap-2 min-w-0 ${taskDone ? 'opacity-60' : ''}`}>
              {task.number_value != null && !Number.isNaN(task.number_value) && (
                <span className="inline-flex items-center text-[10px] font-medium tabular-nums text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/40 px-1.5 py-0.5 rounded">
                  {formatAggregationNumber(task.number_value)}
                </span>
              )}
              {task.due_date && (
                <span className={`inline-flex items-center gap-0.5 text-[10px] ${
                  isOverdue ? 'text-red-500' : 'text-gray-500 dark:text-gray-400'
                }`}>
                  <Calendar className="w-3 h-3" />
                  {formatArgentinaDate(task.due_date)}
                </span>
              )}
              {total > 0 && (
                <span className="inline-flex items-center gap-0.5 text-[10px] text-gray-500 dark:text-gray-400">
                  <CheckSquare className="w-3 h-3" />
                  {checklistDone}/{total}
                </span>
              )}
              {commentsCount > 0 && (
                <span className="inline-flex items-center gap-0.5 text-[10px] text-gray-500 dark:text-gray-400">
                  <MessageSquare className="w-3 h-3" />
                  {commentsCount}
                </span>
              )}
              {attachmentsCount > 0 && imageAttachments.length === 0 && (
                <span className="inline-flex items-center gap-0.5 text-[10px] text-gray-500 dark:text-gray-400">
                  <Paperclip className="w-3 h-3" />
                  {attachmentsCount}
                </span>
              )}
              {fileAttachments.length > 0 && imageAttachments.length > 0 && (
                <span className="inline-flex items-center gap-0.5 text-[10px] text-gray-500 dark:text-gray-400">
                  <Paperclip className="w-3 h-3" />
                  {fileAttachments.length}
                </span>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectTaskCard;
